import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { openLateConnect } from 'src/api/services/late-service';
import { axiosInstance } from 'src/api/client';
import { useAppAuth } from 'src/context/use-app-auth';
import { firestoreDb } from 'src/api/client';
import { BASE_API_URL } from 'src/app/environment';
import { collection, onSnapshot } from 'firebase/firestore';
import { LocationsCollection } from 'src/api/collections';

export interface AppInfo {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

export interface AppSection {
  label: string;
  apps: AppInfo[];
}

/* eslint-disable @typescript-eslint/no-unused-vars */
interface ConnectAppsContextType {
  connectedApps: string[];
  availableApps: AppInfo[];
  connectApp(id: string): Promise<void>;
  disconnectApp(id: string): Promise<void>;
}

const ConnectAppsContext = createContext<ConnectAppsContextType | undefined>(
  undefined,
);

const DEFAULT_APP_SECTIONS: AppSection[] = [
  {
    label: 'Social Media',
    apps: [
      {
        id: 'facebook',
        name: 'Facebook',
        description: 'Connect your Facebook account to enable publishing.',
        enabled: true,
      },
      {
        id: 'twitter',
        name: 'Twitter (X)',
        description:
          'Connect your Twitter/X account via Late to enable publishing.',
        enabled: true,
      },
      {
        id: 'instagram',
        name: 'Instagram',
        description: 'Connect your Instagram account to enable publishing.',
        enabled: true,
      },
      {
        id: 'tiktok',
        name: 'TikTok',
        description: 'Connect your TikTok account to enable publishing.',
        enabled: true,
      },
    ],
  },
  {
    label: 'Business',
    apps: [
      {
        id: 'clover',
        name: 'Clover',
        description: 'Connect your Clover POS for business integration.',
        enabled: false,
      },
    ],
  },
];

export const ConnectAppsProvider = ({ children }: { children: ReactNode }) => {
  const [connectedApps, setConnectedApps] = useState<string[]>([]);
  const { location } = useAppAuth();

  // Load integrations from Firestore and keep in sync
  useEffect(() => {
    if (!location?.key) {
      setConnectedApps([]);
      return;
    }
    const integrationsRef = collection(
      firestoreDb,
      `${LocationsCollection}/${location.key}/integrations`,
    );
    // Listen for real-time updates
    const unsubscribe = onSnapshot(integrationsRef, snap => {
      const connected: string[] = [];
      snap.forEach(doc => {
        const data = doc.data();
        if (data.status === 'connected') {
          connected.push(doc.id);
        }
      });
      setConnectedApps(connected);
    });

    return () => unsubscribe();
  }, [location?.key]);

  const connectApp = async (id: string) => {
    if (id === 'twitter') {
      await openLateConnect('twitter', location?.key);
    }
    if (id === 'instagram') {
      await openLateConnect('instagram', location?.key);
    }
    // No local state update; Firestore listener will update after callback
  };

  const disconnectApp = async (id: string) => {
    if (!location?.key) return;
    // Call backend API to disconnect using axios
    await axiosInstance.post(
      `${BASE_API_URL}/integrations/connect/disconnect/${id}?lid=${location.key}`,
    );
    // Firestore listener will update state
  };

  // Flatten all apps for legacy consumers, but expose sections for UI
  const availableApps = DEFAULT_APP_SECTIONS.flatMap(section => section.apps);

  return React.createElement(
    ConnectAppsContext.Provider,
    {
      value: {
        connectedApps,
        availableApps,
        connectApp,
        disconnectApp,
      },
    },
    children,
  );
};

// Export sections for use in UI
export const CONNECT_APP_SECTIONS = DEFAULT_APP_SECTIONS;

export function useConnectApps() {
  const ctx = useContext(ConnectAppsContext);
  if (!ctx)
    throw new Error('useConnectApps must be used within ConnectAppsProvider');
  return ctx;
}
